import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building, MapPin, Phone, Check, ArrowRight } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { LoadingButton } from '../components/ui/LoadingButton'
import toast from 'react-hot-toast'
import { useAuthStore } from '../stores/authStore'
import { useAuditorStore } from '../stores/auditorStore'

export const Onboarding = () => {
  const navigate = useNavigate()
  const { user, getCurrentUser } = useAuthStore()
  const { isAuditor } = useAuditorStore()
  const [isLoading, setIsLoading] = useState(false)
  const [setupType, setSetupType] = useState<'business' | 'individual' | null>(null)

  const [businessDetails, setBusinessDetails] = useState({
    business_name: '',
    business_type: 'retail' as const,
    gst_number: '',
    pan_number: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: ''
  })

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
    'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep'
  ]

  const businessTypes = [
    { value: 'retail', label: 'Retail Store' },
    { value: 'wholesale', label: 'Wholesale Business' },
    { value: 'restaurant', label: 'Restaurant/Food Service' },
    { value: 'pharmacy', label: 'Pharmacy/Medical' },
    { value: 'electronics', label: 'Electronics Store' },
    { value: 'clothing', label: 'Clothing/Fashion' },
    { value: 'grocery', label: 'Grocery Store' },
    { value: 'services', label: 'Services' },
    { value: 'other', label: 'Other' }
  ]

  const validateBusinessDetails = () => {
    const { business_name, address, city, state, pincode, phone } = businessDetails

    if (!business_name.trim()) {
      toast.error('Business name is required')
      return false
    }

    if (!address.trim()) {
      toast.error('Business address is required')
      return false
    }

    if (!city.trim()) {
      toast.error('City is required')
      return false
    }

    if (!state.trim()) {
      toast.error('State is required')
      return false
    }

    if (!pincode.trim() || pincode.length !== 6) {
      toast.error('Valid 6-digit pincode is required')
      return false
    }

    if (!phone.trim() || phone.length < 10) {
      toast.error('Business phone number is required')
      return false
    }

    return true
  }

  const handleSkip = async () => {
    setIsLoading(true)
    try {
      // Call API to mark onboarding as skipped (individual user)
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8080/api'}/auth/onboarding/skip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      const data = await response.json()
      console.log('Skip onboarding response:', data)

      if (!response.ok) {
        console.error('Skip onboarding failed:', data)
        throw new Error(data.message || 'Failed to skip onboarding')
      }

      // Update user state immediately with the returned user data
      if (data.user) {
        console.log('Updating user state with:', data.user)
        useAuthStore.setState({ user: data.user })
      }

      // Refresh user data
      await getCurrentUser()
      toast.success('Account setup completed as individual user')

      // Redirect to main dashboard for all users
      navigate('/')
    } catch (error: any) {
      console.error('Skip onboarding error:', error)
      toast.error(error.message || 'Failed to complete setup')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBusinessSetup = async () => {
    if (!validateBusinessDetails()) {
      return
    }

    setIsLoading(true)
    try {
      // Call API to save business details
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8080/api'}/auth/onboarding/business`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(businessDetails)
      })

      const data = await response.json()
      console.log('Business onboarding response:', data)

      if (!response.ok) {
        console.error('Business onboarding failed:', data)
        throw new Error(data.message || 'Failed to save business details')
      }

      if (!data.success) {
        console.error('Business onboarding unsuccessful:', data)
        throw new Error(data.message || 'Failed to save business details')
      }

      // Update user state immediately with the returned user data
      if (data.user) {
        console.log('Updating user state with:', data.user)
        useAuthStore.setState({ user: data.user })
      }

      // Also refresh user data from server to ensure sync
      console.log('Refreshing user data...')
      await getCurrentUser()

      toast.success('Business setup completed successfully!')

      // Redirect to main dashboard for all users
      navigate('/')
    } catch (error: any) {
      console.error('Business setup error:', error)
      toast.error(error.message || 'Failed to save business details')
    } finally {
      setIsLoading(false)
    }
  }

  if (!setupType) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50 flex items-center justify-center p-4">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to digiBills! 🎉</h1>
            <p className="text-gray-600">Let's set up your account</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Business Setup Card */}
            <Card
              className="cursor-pointer hover:shadow-xl transition-all border-2 hover:border-orange-400"
              onClick={() => setSetupType('business')}
            >
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
                    <Building className="w-10 h-10 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Business Account</h3>
                  <p className="text-gray-600 mb-4">
                    Set up your business details, GST information, and start billing
                  </p>
                  <ul className="text-sm text-gray-600 space-y-2 text-left">
                    <li className="flex items-center">
                      <Check className="w-4 h-4 text-green-600 mr-2" />
                      Business profile & details
                    </li>
                    <li className="flex items-center">
                      <Check className="w-4 h-4 text-green-600 mr-2" />
                      GST & tax compliance
                    </li>
                    <li className="flex items-center">
                      <Check className="w-4 h-4 text-green-600 mr-2" />
                      Multi-user access
                    </li>
                    <li className="flex items-center">
                      <Check className="w-4 h-4 text-green-600 mr-2" />
                      Advanced features
                    </li>
                  </ul>
                  <Button className="w-full mt-6">
                    Setup Business <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Individual Setup Card */}
            <Card
              className="cursor-pointer hover:shadow-xl transition-all border-2 hover:border-blue-400"
              onClick={() => setSetupType('individual')}
            >
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                    <Check className="w-10 h-10 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Individual Account</h3>
                  <p className="text-gray-600 mb-4">
                    Skip business setup and start using the POS system right away
                  </p>
                  <ul className="text-sm text-gray-600 space-y-2 text-left">
                    <li className="flex items-center">
                      <Check className="w-4 h-4 text-green-600 mr-2" />
                      Quick setup
                    </li>
                    <li className="flex items-center">
                      <Check className="w-4 h-4 text-green-600 mr-2" />
                      Basic billing features
                    </li>
                    <li className="flex items-center">
                      <Check className="w-4 h-4 text-green-600 mr-2" />
                      Add business details later
                    </li>
                    <li className="flex items-center">
                      <Check className="w-4 h-4 text-green-600 mr-2" />
                      Perfect for personal use
                    </li>
                  </ul>
                  <Button variant="outline" className="w-full mt-6">
                    Continue as Individual <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  if (setupType === 'individual') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Individual Account Setup</CardTitle>
            <CardDescription>You can add business details anytime from settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">You're all set!</h4>
              <p className="text-sm text-blue-800">
                Your account is ready to use. You can start managing your inventory and creating bills right away.
              </p>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setSetupType(null)} className="flex-1">
                Back
              </Button>
              <LoadingButton
                onClick={handleSkip}
                isLoading={isLoading}
                loadingText="Setting up..."
                className="flex-1"
              >
                Continue to Dashboard
              </LoadingButton>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <CardTitle>Business Information</CardTitle>
          <CardDescription>Tell us about your business</CardDescription>
        </CardHeader>

        <CardContent>
          <form className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">Business Name *</label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    value={businessDetails.business_name}
                    onChange={(e) => setBusinessDetails({ ...businessDetails, business_name: e.target.value })}
                    placeholder="Enter business name"
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">Business Type *</label>
                <select
                  value={businessDetails.business_type}
                  onChange={(e) => setBusinessDetails({ ...businessDetails, business_type: e.target.value as any })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                >
                  {businessTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">GST Number (Optional)</label>
                <Input
                  value={businessDetails.gst_number}
                  onChange={(e) => setBusinessDetails({ ...businessDetails, gst_number: e.target.value })}
                  placeholder="GST Number"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">PAN Number (Optional)</label>
                <Input
                  value={businessDetails.pan_number}
                  onChange={(e) => setBusinessDetails({ ...businessDetails, pan_number: e.target.value })}
                  placeholder="PAN Number"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">Business Address *</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
                  <textarea
                    value={businessDetails.address}
                    onChange={(e) => setBusinessDetails({ ...businessDetails, address: e.target.value })}
                    placeholder="Enter complete address"
                    className="w-full pl-10 pt-2 pb-2 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                    rows={3}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">City *</label>
                <Input
                  value={businessDetails.city}
                  onChange={(e) => setBusinessDetails({ ...businessDetails, city: e.target.value })}
                  placeholder="City"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">State *</label>
                <select
                  value={businessDetails.state}
                  onChange={(e) => setBusinessDetails({ ...businessDetails, state: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                >
                  <option value="">Select State</option>
                  {indianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Pincode *</label>
                <Input
                  value={businessDetails.pincode}
                  onChange={(e) => setBusinessDetails({ ...businessDetails, pincode: e.target.value })}
                  placeholder="Pincode"
                  maxLength={6}
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Business Phone *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    value={businessDetails.phone}
                    onChange={(e) => setBusinessDetails({ ...businessDetails, phone: e.target.value })}
                    placeholder="Business Phone"
                    className="pl-10"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setSetupType(null)} className="flex-1">
                Back
              </Button>
              <LoadingButton
                onClick={handleBusinessSetup}
                isLoading={isLoading}
                loadingText="Saving..."
                className="flex-1"
              >
                Complete Setup
              </LoadingButton>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
